import { defer, Deferred, Promise } from 'q'

import { Task, TaskStatus } from './task'
import { Call } from './call'
import { now } from './stats'
import { TestReport } from './test-report'

const PREFIX_INFO = '[INFO]'
const PREFIX_OK = '[OK]'
const PREFIX_FAILED = '[FAILED]'
const PREFIX_WARNING = '[WARNING]'

export class Test implements Task {
  report: TestReport
  name: string
  call?: Call
  deferred?: Deferred<unknown>
  testFunction: (test: Test) => void

  state: TaskStatus = 'idle'
  wasOpened = false
  opened = false
  isDisabled = false
  canceled = false

  errorCount = 0
  warningCount = 0
  successCount = 0

  startTime = -1
  endTime = -1

  progressValue: any = null
  output: any[] = []

  get settings() {
    return this.report.settings
  }

  get duration() {
    const endTime = this.endTime === -1 ? now() : this.endTime

    return (
      Math.round(((endTime - this.startTime) / 1000) * 100) / 100
    ).toFixed(2)
  }

  traceTestEvent: any

  constructor(report: TestReport, name: string, testFunction: any) {
    this.name = name
    this.report = report
    this.testFunction = testFunction
  }

  run() {
    const deferred = defer()
    this.deferred = deferred

    this.endTime = -1
    this.startTime = now()
    this.successCount = 0
    this.warningCount = 0
    this.errorCount = 0
    this.output = []
    this.progressValue = null

    this.state = 'running'
    this.wasOpened = this.wasOpened || false
    this.opened = true

    this.traceTestEvent = this.report.traceEventAsync('test-run')

    if (!this.isDisabled) {
      try {
        this.testFunction(this)
      } catch (testErr) {
        this.reportFatal(testErr as Error)
      }
    } else {
      this.reportInfo('Test is disabled.')
      this.done()
    }

    return this.deferred.promise
  }

  cancel() {
    this.call?.close()
    this.canceled = true
    this.done()
  }

  traceEventInstant(name: string, args: any) {
    this.report.traceEventInstant(name, args)
  }

  traceEventAsync(name: string) {
    return this.report.traceEventAsync(name)
  }

  expectEquals(expected: any, actual: any, failMsg: string, okMsg: string) {
    if (expected !== actual) {
      this.reportError(
        'Failed expectation: ' + expected + ' !== ' + actual + ': ' + failMsg,
      )
    } else if (okMsg) {
      this.reportSuccess(okMsg)
    }
  }

  setProgress(value: any) {
    this.progressValue = value
    this.deferred.notify(this)
  }

  reportSuccess(success: string) {
    this.successCount++
    this.reportMessage(PREFIX_OK, success)
    this.traceTestEvent({ success })
  }

  reportError(error: string | Error) {
    this.errorCount++
    this.reportMessage(PREFIX_FAILED, error)
    this.traceTestEvent({ error })
  }

  reportWarning(warning: string) {
    this.warningCount++
    this.reportMessage(PREFIX_WARNING, warning)
    this.traceTestEvent({ warning })
  }

  reportInfo(info: string) {
    this.reportMessage(PREFIX_INFO, info)
    this.traceTestEvent({ info })
  }

  reportFatal(str: string | Error) {
    if (this.canceled) {
      return
    }

    if (str instanceof Error) {
      str = str.message
    }

    this.reportError(str)
    this.done()
  }

  reportMessage(prefix: string, message: string | Error) {
    if (message instanceof Error) {
      message = message.message
    }

    this.output.push({ prefix, message, time: Date.now() })
    this.deferred.notify(this)
  }

  done() {
    let status: TaskStatus

    this.endTime = now()

    if (this.isDisabled) {
      status = 'disabled'
    } else if (this.canceled) {
      status = 'canceled'
    } else {
      const isSuccess =
        this.errorCount === 0 && this.successCount + this.warningCount > 0
      status = isSuccess ? 'success' : 'failure'
    }

    this.state = status
    this.opened = status === 'success'

    this.setProgress(null)
    this.traceTestEvent({ status })

    if (this.opened) {
      this.deferred?.resolve(this)
    } else {
      this.deferred?.reject(this)
    }
  }
}
